import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';

const Payroll = () => {
    const { authFetch, user } = useContext(AppContext);
    const { t } = useTranslation();
    const isAdmin = user?.role === 'Admin' || user?.role === 'Manager';

    const [payrolls, setPayrolls] = useState([]);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [isGenerating, setIsGenerating] = useState(false);

    const fetchPayrolls = () => {
        authFetch(`/api/payroll?month=${month}&year=${year}`)
            .then(r => r.json())
            .then(d => setPayrolls(Array.isArray(d) ? d : []));
    };

    useEffect(() => {
        fetchPayrolls();
    }, [month, year]);

    const handleGenerate = async () => {
        setIsGenerating(true);
        try {
            const res = await authFetch('/api/payroll', {
                method: 'POST',
                body: JSON.stringify({ action: 'generate', month, year })
            });
            const generated = await res.json();
            if (res.ok) {
                // Save them automatically
                for (const slip of generated) {
                    await authFetch('/api/payroll', {
                        method: 'POST',
                        body: JSON.stringify({ action: 'save', month, year, status: 'Draft', ...slip })
                    });
                }
                fetchPayrolls();
            }
        } catch (e) {
            console.error(e);
        }
        setIsGenerating(false);
    };

    const handleMarkPaid = async (id) => {
        if (!window.confirm(t('Mark this slip as Paid?'))) return;
        const res = await authFetch(`/api/payroll/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ status: 'Paid' })
        });
        if (res.ok) fetchPayrolls();
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Delete this payroll record?'))) return;
        const res = await authFetch(`/api/payroll/${id}`, { method: 'DELETE' });
        if (res.ok) fetchPayrolls();
    };

    const columns = [
        { 
            header: t('Employee'), accessor: 'employee_name',
            render: r => (
                <div>
                    <span className="font-bold text-gray-800">{r.employee_name}</span>
                    <p className="text-xs text-gray-400">{r.department_name}</p>
                </div>
            )
        },
        { 
            header: t('Period'), accessor: 'period',
            render: r => <span className="text-xs font-bold text-gray-500 uppercase">{r.month}/{r.year}</span>
        },
        { 
            header: t('Base Salary'), accessor: 'base_salary',
            render: r => <span className="font-medium text-gray-600">{parseFloat(r.base_salary).toLocaleString()}</span>
        },
        { 
            header: t('Additions'), accessor: 'additions',
            render: r => <span className="font-bold text-emerald-600">+{parseFloat(r.additions).toLocaleString()}</span>
        },
        { 
            header: t('Deductions'), accessor: 'deductions',
            render: r => <span className="font-bold text-red-600">-{parseFloat(r.deductions).toLocaleString()}</span>
        },
        { 
            header: t('Net Salary'), accessor: 'net_salary',
            render: r => <span className="font-black text-indigo-700 text-lg">{parseFloat(r.net_salary).toLocaleString()} CFA</span>
        },
        { 
            header: t('Status'), accessor: 'status',
            render: r => (
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${r.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {t(r.status)}
                </span>
            )
        },
        {
            header: t('Actions'), accessor: 'actions',
            render: r => (
                <div className="flex gap-2">
                    {isAdmin && r.status === 'Draft' && (
                        <button className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs hover:bg-emerald-200 font-bold" onClick={() => handleMarkPaid(r.id)}>{t('Pay')}</button>
                    )}
                    {isAdmin && (
                        <button className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200" onClick={() => handleDelete(r.id)}>{t('Del')}</button>
                    )}
                </div>
            )
        }
    ];

    const totalNet = payrolls.reduce((sum, p) => sum + parseFloat(p.net_salary), 0);

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">{t('Payroll Processing')}</h2>
                    <p className="text-gray-500 text-sm mt-1">{t('Generate and manage monthly salary slips')}</p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <select className="border p-2 rounded shadow-sm focus:ring-2 focus:ring-indigo-500" value={month} onChange={e => setMonth(e.target.value)}>
                        {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>)}
                    </select>
                    <input type="number" min="2020" max="2100" className="border p-2 rounded shadow-sm w-24" value={year} onChange={e => setYear(e.target.value)} />
                    
                    {isAdmin && (
                        <button onClick={handleGenerate} disabled={isGenerating} className={`text-white px-4 py-2 rounded shadow transition font-bold ${isGenerating ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
                            {isGenerating ? t('Generating...') : `⚙️ ${t('Auto-Generate Drafts')}`}
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex justify-between items-center shadow-sm">
                <div>
                    <p className="text-xs uppercase font-bold text-indigo-500">{t('Total Net Payroll for Period')}</p>
                    <p className="text-3xl font-black text-indigo-800">{totalNet.toLocaleString()} <span className="text-sm font-normal">CFA</span></p>
                </div>
            </div>

            <DataTable columns={columns} data={payrolls} />
        </div>
    );
};

export default Payroll;
